// โหลดข้อมูลหน้าความจุบัญชี: รายการธนาคาร (limit ปัจจุบัน) + รายงานของจริงรายวัน
// สองคำขอแยกกันด้วย allSettled — รายงานพัง/tenant ยังไม่ลง RPC ไม่ทำให้ส่วนความจุ/what-if ใช้ไม่ได้
import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { merchantApi } from '../../../api/config';
import type { AdminBankLite, BankDayActual, CapacityReport } from './types';

export interface BankCapacityData {
  /** บัญชีฝากที่ใช้งานอยู่ (ตัดบัญชีถอน/ปิด — pool เดียวกับ gate) */
  banks: AdminBankLite[];
  /** tenant เปิดเพดานช่วงเวลาแล้ว (window_limit_supported) */
  windowSupported: boolean;
  /** ของจริงต่อบัญชี key = bank_id */
  actual: Record<string, BankDayActual>;
  /** ทุกแถวจาก RPC รวมบัญชีที่ไม่อยู่ใน pool แล้ว (ปิด/ถอน) — ใช้รวมกราฟรายชั่วโมงให้เป็นยอดจริงทั้งวัน */
  allActual: BankDayActual[];
  /** false = tenant ยังไม่ลง RPC หรือโหลดไม่สำเร็จ */
  reportSupported: boolean;
  reportDate: string | null;
  /** บางบัญชีมี order เกิน cap → พีคอาจต่ำกว่าจริง */
  truncated: boolean;
}

const EMPTY: BankCapacityData = {
  banks: [], windowSupported: false, actual: {}, allActual: [], reportSupported: false, reportDate: null, truncated: false,
};

type ReportSlice = Pick<BankCapacityData, 'actual' | 'allActual' | 'reportSupported' | 'reportDate' | 'truncated'>;

const noReport = (date: string): ReportSlice => ({
  actual: {}, allActual: [], reportSupported: false, reportDate: date, truncated: false,
});

const applyReport = (rep: CapacityReport | undefined, date: string): ReportSlice => {
  if (!rep?.supported) return noReport(date);
  const actual: Record<string, BankDayActual> = {};
  rep.banks.forEach((b) => { actual[b.bank_id] = b; });
  return { actual, allActual: rep.banks, reportSupported: true, reportDate: rep.date, truncated: rep.banks.some((b) => b.truncated) };
};

export const useBankCapacity = () => {
  const [data, setData] = useState<BankCapacityData>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  const load = useCallback(async (date: string) => {
    setLoading(true);
    const [banksRes, reportRes] = await Promise.allSettled([
      merchantApi.get('/admin/banks'),
      merchantApi.get('/admin/banks/capacity-report', { params: { date } }),
    ]);

    setData((prev) => {
      let next: BankCapacityData = { ...prev };

      if (banksRes.status === 'fulfilled') {
        const rows = (banksRes.value.data?.data || []) as AdminBankLite[];
        next.banks = rows.filter((b) => b.is_active && !b.is_withdraw);
        next.windowSupported = banksRes.value.data?.window_limit_supported === true;
      } else {
        toast.error('โหลดรายการธนาคารไม่สำเร็จ');
      }

      if (reportRes.status === 'fulfilled') {
        next = { ...next, ...applyReport(reportRes.value.data?.data as CapacityReport | undefined, date) };
      } else {
        const err = reportRes.reason;
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
        if (status === 400) {
          toast.error(message || 'วันที่ไม่ถูกต้อง'); // คงรายงานเดิมไว้
        } else if (status === 404) {
          next = { ...next, ...noReport(date) }; // backend เก่ายังไม่มี route — เงียบ
        } else {
          toast.error(message || 'โหลดรายงานความจุไม่สำเร็จ');
          next = { ...next, ...noReport(date) };
        }
      }
      return next;
    });

    setLoadedAt(new Date());
    setLoading(false);
  }, []);

  return { ...data, loading, loadedAt, load };
};
